import React from "react";
import { asLines } from "../../lib/sentences.js";
import { pronounForms } from "../../lib/sections.js";

/**
 * "Your role" as the eye wants it (Plan H): line breaks become bullets, and
 * a run-on of three or more sentences is split one per bullet. One or two
 * sentences stay a paragraph.
 */
function RoleText({ text }) {
  const lines = asLines(text);
  if (lines.length <= 1) return <p style={styles.rowText}>{lines[0] || text}</p>;
  return (
    <ul style={styles.roleList}>
      {lines.map((l, i) => <li key={i} style={styles.roleItem}>{l}</li>)}
    </ul>
  );
}

function GoalCard({ goal, pronouns = "" }) {
  const f = pronounForms(pronouns);
  return (
    <div style={styles.card}>
      <p style={styles.translated}>{goal.translated_text}</p>
      {goal.purpose && (
        <div style={styles.row}>
          <span style={styles.rowLabel}>Why it matters</span>
          <p style={styles.rowText}>{goal.purpose}</p>
        </div>
      )}
      {goal.caregiver_role && (
        <div style={{ ...styles.row, ...styles.roleRow }}>
          <span style={styles.rowLabel}>Your role</span>
          <RoleText text={goal.caregiver_role} />
        </div>
      )}
      {goal.status_progress && (
        <div style={styles.row}>
          <span style={styles.rowLabel}>Where {f.subj} {f.plural ? "are" : "is"} now</span>
          <p style={styles.rowText}>{goal.status_progress}</p>
        </div>
      )}
    </div>
  );
}

export default function GoalList({ goals = [], pronouns = "" }) {
  if (!goals.length) return null;
  return (
    <div style={styles.list}>
      {goals.map(g => <GoalCard key={g.id} goal={g} pronouns={pronouns} />)}
    </div>
  );
}

const styles = {
  list: { display: "flex", flexDirection: "column", gap: "var(--space-5)" },
  card: {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-lg)",
    padding: "var(--space-6)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  },
  translated: {
    fontSize: "var(--text-base)",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-text-primary)",
    lineHeight: "var(--leading-normal)",
  },
  row: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
    paddingTop: "var(--space-3)",
    borderTop: "1px solid var(--color-border)",
  },
  roleRow: {
    background: "var(--color-accent-subtle)",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-3)",
    border: "none",
    marginTop: 0,
  },
  rowLabel: {
    fontSize: "var(--text-xs)",
    fontWeight: "var(--weight-semibold)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--color-text-tertiary)",
  },
  rowText: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-secondary)",
    lineHeight: "var(--leading-normal)",
    whiteSpace: "pre-line",
  },
  roleList: {
    margin: 0,
    padding: "0 0 0 1.1em",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  },
  roleItem: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-secondary)",
    lineHeight: "var(--leading-normal)",
  },
};
